// src/components/doctor/Attendance.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { 
  Edit, 
  Trash2, 
  RefreshCw, 
  Plus, 
  Search, 
  Clock, 
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  LogIn,
  LogOut,
  FileText,
  User
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "../ui/use-toast";
import {
  checkIn,
  checkOut,
  getAttendance,
  getAttendanceStats,
  submitLeaveRequest,
  getLeaveRequests,
  approveLeaveRequest,
  updateAttendance,
  deleteAttendance,
} from "../../utils/attendanceApi";
import { useAuth } from "../../contexts/PhpAuthContext";

const cls = (...parts) => parts.filter(Boolean).join(" ");

const formatDate = (v) => {
  if (!v) return "—";
  // If v is already a date string in YYYY-MM-DD format, parse it with IST timezone
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) {
    // Parse as IST date (treat as noon IST to avoid timezone issues)
    const [year, month, day] = v.split('T')[0].split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      timeZone: "Asia/Kolkata",
    });
  }
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "Asia/Kolkata",
  });
};

const formatTime = (v) => {
  if (!v) return "—";
  // Convert 24-hour format to 12-hour AM/PM format
  const [hours, minutes] = v.split(':');
  const hour24 = parseInt(hours, 10);
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  return `${hour12}:${minutes} ${ampm}`;
};

const formatTotalHours = (totalHours) => {
  if (!totalHours && totalHours !== 0) return "—";
  const hours = parseFloat(totalHours);
  if (hours < 1) {
    // If less than 1 hour, show in minutes
    const minutes = Math.round(hours * 60);
    return `${minutes} mins`;
  } else {
    // If 1 hour or more, show in hours
    // Round to 1 decimal place if needed
    const roundedHours = Math.round(hours * 10) / 10;
    return `${roundedHours} hrs`;
  }
};

// Calculate working days from 1st of the month to current date (Mon-Sat, excluding Sundays)
const getWorkingDaysForMonth = () => {
  // Get current date in IST timezone
  const now = new Date();
  const istDateString = now.toLocaleDateString("en-CA", {
    timeZone: "Asia/Kolkata"
  }); // Returns YYYY-MM-DD format
  
  const [year, month, day] = istDateString.split('-').map(Number);
  const currentDate = new Date(year, month - 1, day); // month is 0-indexed in Date
  
  // Get first day of the month
  const firstDay = new Date(year, month - 1, 1);
  
  let workingDays = 0;
  
  // Count working days from 1st of the month to current date (Monday to Saturday, excluding Sundays)
  for (let d = firstDay.getDate(); d <= currentDate.getDate(); d++) {
    const dateToCheck = new Date(year, month - 1, d);
    const dayOfWeek = dateToCheck.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Count Mon-Sat (exclude Sunday which is 0)
    if (dayOfWeek !== 0) {
      workingDays++;
    }
  }
  
  return workingDays;
};

// Calculate working days for a date range (Mon-Sat, excluding Sundays)
const getWorkingDaysForDateRange = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  let workingDays = 0;
  
  const current = new Date(start);
  while (current <= end) {
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0) { // Exclude Sunday
      workingDays++;
    }
    current.setDate(current.getDate() + 1);
  }
  
  return workingDays;
};

// Get month name from date string (YYYY-MM-DD)
const getMonthName = (dateString) => {
  const [year, month] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'long' });
};

// Get gradient background class for each month
const getMonthGradient = (monthName) => {
  const gradients = {
    'January': 'bg-gradient-to-br from-blue-500/25 via-blue-400/15 to-transparent border border-blue-500/40',
    'February': 'bg-gradient-to-br from-pink-500/25 via-pink-400/15 to-transparent border border-pink-500/40',
    'March': 'bg-gradient-to-br from-green-500/25 via-green-400/15 to-transparent border border-green-500/40',
    'April': 'bg-gradient-to-br from-yellow-500/25 via-yellow-400/15 to-transparent border border-yellow-500/40',
    'May': 'bg-gradient-to-br from-purple-500/25 via-purple-400/15 to-transparent border border-purple-500/40',
    'June': 'bg-gradient-to-br from-indigo-500/25 via-indigo-400/15 to-transparent border border-indigo-500/40',
    'July': 'bg-gradient-to-br from-red-500/25 via-red-400/15 to-transparent border border-red-500/40',
    'August': 'bg-gradient-to-br from-orange-500/25 via-orange-400/15 to-transparent border border-orange-500/40',
    'September': 'bg-gradient-to-br from-teal-500/25 via-teal-400/15 to-transparent border border-teal-500/40',
    'October': 'bg-gradient-to-br from-amber-500/25 via-amber-400/15 to-transparent border border-amber-500/40',
    'November': 'bg-gradient-to-br from-cyan-500/25 via-cyan-400/15 to-transparent border border-cyan-500/40',
    'December': 'bg-gradient-to-br from-violet-500/25 via-violet-400/15 to-transparent border border-violet-500/40',
  };
  return gradients[monthName] || 'bg-gradient-to-br from-gray-500/25 via-gray-400/15 to-transparent border border-gray-500/40';
};

const formatDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const LEAVE_TYPE_OPTIONS = [
  { value: "Sick Leave", label: "Sick Leave" },
  { value: "Casual Leave", label: "Casual Leave" },
  { value: "Earned Leave", label: "Earned Leave" },
  { value: "Emergency Leave", label: "Emergency Leave" },
  { value: "Other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "Present", label: "Present" },
  { value: "Checked Out", label: "Checked Out" },
  { value: "Absent", label: "Absent" },
  { value: "Half Day", label: "Half Day" },
  { value: "Leave", label: "Leave" },
];

const Attendance = ({ initialTab = "checkin", viewAsAdmin = false, isAdmin = false }) => {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [attendance, setAttendance] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  // For admins: store all active check-ins (checked in but not checked out)
  const [allActiveAttendance, setAllActiveAttendance] = useState([]);
  
  // Filters
  const [filter, setFilter] = useState("month");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [leaveStatusFilter, setLeaveStatusFilter] = useState("all");
  const [doctorStatsFilter, setDoctorStatsFilter] = useState("month"); // "week" or "month"
  const [doctorStatsSearch, setDoctorStatsSearch] = useState(""); // Search term for doctor statistics
  const [myAttendanceFilter, setMyAttendanceFilter] = useState("month"); // "week" or "month" for doctor's own attendance
  const [myAttendanceSearch, setMyAttendanceSearch] = useState(""); // Search term for doctor's own attendance
  
  // Modals
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  // Initialize overtimeEnabled from localStorage or default to false
  const [overtimeEnabled, setOvertimeEnabled] = useState(() => {
    const stored = localStorage.getItem('overtimeEnabled');
    return stored === 'true';
  });

  // Track if this is the initial load to force checkin tab only once
  const hasInitialized = useRef(false);

  // Update activeTab when initialTab prop changes, but respect check-in status on initial load only
  useEffect(() => {
    // On initial load, if user is checked in but not checked out, switch to checkin tab
    if (!hasInitialized.current && todayAttendance && todayAttendance.check_in_time && !todayAttendance.check_out_time) {
      setActiveTab('checkin');
      hasInitialized.current = true;
      return;
    }
    
    // Mark as initialized after first check
    if (!hasInitialized.current) {
      hasInitialized.current = true;
    }
    
    // Otherwise, allow initialTab to set the active tab
    if (initialTab && ['checkin', 'attendance', 'all_attendance', 'leave', 'approve_leave'].includes(initialTab)) {
      setActiveTab(initialTab);
    }
  }, [initialTab, todayAttendance]);

  // Check today's attendance status and refresh periodically to check for auto-checkout
  useEffect(() => {
    if (profile) {
      fetchTodayAttendance();
      // Refresh every minute to check for auto-checkout
      const refreshInterval = setInterval(() => {
        fetchTodayAttendance();
      }, 60000); // Every 60 seconds

      return () => clearInterval(refreshInterval);
    }
  }, [profile, viewAsAdmin]);

  // Update current date/time every second (IST)
  // Also force re-render when todayAttendance changes to update working time
  useEffect(() => {
    const updateDateTime = () => {
      // Get current time - will use browser's local timezone (IST if in India)
      const now = new Date();
      setCurrentDateTime(now);
    };
    
    updateDateTime(); // Initial update
    const timer = setInterval(updateDateTime, 1000);

    return () => clearInterval(timer);
  }, [todayAttendance?.check_in_time]); // Re-run when check-in time changes

  const fetchTodayAttendance = async () => {
    try {
      // Get today's date in IST (Asia/Kolkata timezone)
      // Use Intl.DateTimeFormat to get date string in IST
      const now = new Date();
      const istFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const today = istFormatter.format(now); // Returns YYYY-MM-DD format
      
      // For admins, fetch ALL active check-ins (checked in but not checked out)
      if (isAdmin || (viewAsAdmin && profile?.role === 'chief_doctor')) {
        // Search for all unchecked-out records (last 7 days)
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const startDate = istFormatter.format(sevenDaysAgo);
        const endDate = today;
        
        const params = {
          start_date: startDate,
          end_date: endDate,
          limit: 100, // Get enough records to find all unchecked-out ones
          view_as_admin: viewAsAdmin && profile?.role === 'chief_doctor'
        };
        const data = await getAttendance(params);
        
        // Filter for records with check_in_time but no check_out_time
        if (data.success && data.attendance && data.attendance.length > 0) {
          const uncheckedOut = data.attendance.filter(a => 
            a.check_in_time && !a.check_out_time
          );
          // Store all active check-ins for admins
          setAllActiveAttendance(uncheckedOut.map(a => ({
            ...a,
            attendance_type: a.attendance_type || 'Regular'
          })));
          
          // Also set the first one as todayAttendance for compatibility
          if (uncheckedOut.length > 0) {
            const firstAttendance = uncheckedOut[0];
            if (firstAttendance.date && firstAttendance.check_in_time) {
              setTodayAttendance({
                ...firstAttendance,
                attendance_type: firstAttendance.attendance_type || 'Regular'
              });
            }
          } else {
            setTodayAttendance(null);
          }
        } else {
          setAllActiveAttendance([]);
          setTodayAttendance(null);
        }
        return; // Exit early for admins
      }
      
      // For regular doctors, fetch their own attendance (existing logic)
      // First, try to get today's attendance - but only active check-ins (not checked out yet)
      // This allows multiple check-in/check-out sessions in the same day
      let params = {
        filter: 'date',
        date: today,
        limit: 10, // Get all records for today to find active ones
        view_as_admin: viewAsAdmin && profile?.role === 'chief_doctor'
      };
      let data = await getAttendance(params);
      
      // Filter for active check-ins only (check_in_time but no check_out_time)
      // This allows users to check in again after completing a checkout session
      if (data.success && data.attendance && data.attendance.length > 0) {
        const activeCheckIns = data.attendance.filter(a => 
          a.check_in_time && !a.check_out_time
        );
        if (activeCheckIns.length > 0) {
          // Get the most recent active check-in
          data.attendance = [activeCheckIns[0]];
        } else {
          // No active check-ins for today, check for unchecked-out records from previous days
          // This handles cases where user checked in yesterday but hasn't checked out yet
          const sevenDaysAgo = new Date(now);
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          const startDate = istFormatter.format(sevenDaysAgo);
          const endDate = today;
          
          params = {
            start_date: startDate,
            end_date: endDate,
            limit: 10, // Get more records to find the unchecked-out one
            view_as_admin: viewAsAdmin && profile?.role === 'chief_doctor'
          };
          data = await getAttendance(params);
          
          // Filter for records with check_in_time but no check_out_time
          if (data.success && data.attendance && data.attendance.length > 0) {
            const uncheckedOut = data.attendance.filter(a => 
              a.check_in_time && !a.check_out_time
            );
            if (uncheckedOut.length > 0) {
              // Get the most recent one (already sorted by date DESC)
              data.attendance = [uncheckedOut[0]];
            } else {
              data.attendance = [];
            }
          } else {
            data.attendance = [];
          }
        }
      } else {
        // No records for today, search for unchecked-out records from previous days
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const startDate = istFormatter.format(sevenDaysAgo);
        const endDate = today;
        
        params = {
          start_date: startDate,
          end_date: endDate,
          limit: 10,
          view_as_admin: viewAsAdmin && profile?.role === 'chief_doctor'
        };
        data = await getAttendance(params);
        
        // Filter for records with check_in_time but no check_out_time
        if (data.success && data.attendance && data.attendance.length > 0) {
          const uncheckedOut = data.attendance.filter(a => 
            a.check_in_time && !a.check_out_time
          );
          if (uncheckedOut.length > 0) {
            data.attendance = [uncheckedOut[0]];
          } else {
            data.attendance = [];
          }
        } else {
          data.attendance = [];
        }
      }
      
      if (data.success && data.attendance && data.attendance.length > 0) {
        const attendance = data.attendance[0];
        
        // Ensure all required fields are present
        // This is critical for displaying Today's Status and Working Time
        if (!attendance.date || !attendance.check_in_time) {
          console.warn('Attendance data missing required fields:', attendance);
          // Don't set incomplete data
          return;
        }
        
        // Check if auto-checkout happened
        const prevAttendance = todayAttendance;
        if (prevAttendance && 
            prevAttendance.check_in_time && 
            !prevAttendance.check_out_time && 
            attendance.check_out_time) {
          // Auto-checkout happened
          const dayOfWeek = new Date().getDay();
          const openingTime = dayOfWeek === 0 ? '10:00:00' : '09:30:00';
          const closingTime = dayOfWeek === 0 ? '19:00:00' : '21:00:00';
          
          if (prevAttendance.attendance_type === 'Overtime' && attendance.check_out_time === openingTime) {
            // Auto-checkout at office opening time (overtime)
            toast({
              title: "Auto Checkout",
              description: `You were automatically checked out at office opening time (${openingTime}).`,
            });
          } else if (prevAttendance.attendance_type !== 'Overtime' && attendance.check_out_time === closingTime) {
            // Auto-checkout at office closing time (regular)
            toast({
              title: "Auto Checkout",
              description: `You were automatically checked out at office closing time (${closingTime}).`,
            });
          }
        }
        
        // Always update with fetched data from API - this is the source of truth
        // This ensures that on page refresh, the attendance data is properly loaded
        // Make sure we preserve the attendance_type if it exists
        const updatedAttendance = {
          ...attendance,
          attendance_type: attendance.attendance_type || 'Regular'
        };
        setTodayAttendance(updatedAttendance);
        
        // If user is checked in during overtime (attendance_type is Overtime), keep checkbox enabled
        // Also keep it enabled if checked in outside office hours (even if not marked as overtime yet)
        if (attendance.check_in_time && !attendance.check_out_time) {
          if (attendance.attendance_type === 'Overtime') {
            setOvertimeEnabled(true);
            localStorage.setItem('overtimeEnabled', 'true');
          } else if (!isWithinOfficeHours) {
            // If checked in outside office hours but not marked as overtime, enable checkbox
            setOvertimeEnabled(true);
            localStorage.setItem('overtimeEnabled', 'true');
          }
        } else if (attendance.check_out_time) {
          // If checked out, reset overtime checkbox
          setOvertimeEnabled(false);
          localStorage.setItem('overtimeEnabled', 'false');
        } else if (attendance.check_out_time) {
          // If checked out, disable overtime checkbox
          setOvertimeEnabled(false);
        }
      } else {
        // If no attendance data is returned, only clear if user is not checked in
        // This preserves the state when navigating between tabs or on refresh
        setTodayAttendance(prev => {
          // If we have a previous state with check-in but no checkout, keep it
          // This ensures Today's Status and Working Time continue to display
          if (prev && prev.check_in_time && !prev.check_out_time) {
            return prev;
          }
          // Otherwise, clear the state
          return null;
        });
      }
    } catch (error) {
      console.error('Error fetching today attendance:', error);
      setTodayAttendance(null);
    }
  };

  // Fetch attendance list
  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const params = {
        filter,
        date: filter === 'date' ? selectedDate : null,
        limit: 200,
        view_as_admin: viewAsAdmin && profile?.role === 'chief_doctor'
      };
      const data = await getAttendance(params);
      if (data.success) {
        setAttendance(data.attendance || []);
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to fetch attendance",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch attendance",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch leave requests
  const fetchLeaveRequests = async () => {
    setLoading(true);
    try {
      const params = {
        status: leaveStatusFilter !== 'all' ? leaveStatusFilter : null,
        limit: 200,
        view_as_admin: viewAsAdmin && profile?.role === 'chief_doctor'
      };
      const data = await getLeaveRequests(params);
      if (data.success) {
        setLeaveRequests(data.leave_requests || []);
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to fetch leave requests",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching leave requests:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch leave requests",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const params = {
        period: filter === 'week' ? 'week' : 'month',
        view_as_admin: viewAsAdmin && profile?.role === 'chief_doctor'
      };
      const data = await getAttendanceStats(params);
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "attendance" || activeTab === "all_attendance") {
      fetchAttendance();
      fetchStats();
    } else if (activeTab === "leave" || activeTab === "approve_leave") {
      fetchLeaveRequests();
    } else if (activeTab === "checkin") {
      // Always fetch today's attendance when checkin tab becomes active
      // This ensures Today's Status and Working Time are displayed correctly
      fetchTodayAttendance();
    }
  }, [activeTab, filter, selectedDate, leaveStatusFilter, viewAsAdmin]);

  // Check if current time is within office hours
  const isWithinOfficeHours = useMemo(() => {
    const nowIST = new Date(currentDateTime.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
    const dayOfWeek = nowIST.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const hours = nowIST.getHours();
    const minutes = nowIST.getMinutes();
    const currentTimeMinutes = hours * 60 + minutes;
    
    if (dayOfWeek === 0) {
      // Sunday: 10:00 AM - 7:00 PM
      const startMinutes = 10 * 60; // 10:00 AM
      const endMinutes = 19 * 60; // 7:00 PM
      return currentTimeMinutes >= startMinutes && currentTimeMinutes <= endMinutes;
    } else {
      // Mon-Sat: 9:30 AM - 9:00 PM
      const startMinutes = 9 * 60 + 30; // 9:30 AM
      const endMinutes = 21 * 60; // 9:00 PM
      return currentTimeMinutes >= startMinutes && currentTimeMinutes <= endMinutes;
    }
  }, [currentDateTime]);


  // Determine attendance type based on office hours and overtime checkbox
  const getAttendanceType = () => {
    if (overtimeEnabled && !isWithinOfficeHours) {
      return 'Overtime';
    }
    return 'Regular';
  };

  // Handle check in
  const handleCheckIn = async () => {
    setSubmitting(true);
    try {
      // Get current IST date and time from the displayed currentDateTime
      // Use the same date that's shown in "Current Date & Time (IST)"
      const nowIST = new Date(currentDateTime.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
      
      // Format date as YYYY-MM-DD using IST timezone (same as displayed in "Current Date & Time (IST)")
      const dateFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      const today = dateFormatter.format(currentDateTime);
      
      // Verify the date matches what's displayed in "Current Date & Time (IST)"
      // The displayed date uses: currentDateTime.toLocaleDateString with timeZone: "Asia/Kolkata"
      // So we ensure our stored date matches that
      
      // Format time as HH:MM:SS (24-hour format)
      const hours = String(nowIST.getHours()).padStart(2, '0');
      const minutes = String(nowIST.getMinutes()).padStart(2, '0');
      const seconds = String(nowIST.getSeconds()).padStart(2, '0');
      const checkInTime = `${hours}:${minutes}:${seconds}`;
      
      // Get attendance type
      const attendanceType = getAttendanceType();
      
      const data = await checkIn(attendanceType);
      if (data.success) {
        // Immediately set todayAttendance with the IST date and time from currentDateTime
        // This ensures the UI updates right away - sections will appear immediately
        const newAttendance = {
          date: today,
          check_in_time: checkInTime,
          check_out_time: null,
          total_hours: null,
          status: 'Present'
        };
        
        setTodayAttendance(newAttendance);
        
        // Keep overtime checkbox enabled if attendance type is Overtime OR if it was already enabled
        // Don't reset it - it should stay checked until checkout
        if (attendanceType === 'Overtime' || overtimeEnabled) {
          setOvertimeEnabled(true);
          localStorage.setItem('overtimeEnabled', 'true');
        }
        
        // Then fetch the complete attendance record in the background
        // Use a longer delay to ensure the immediate state is visible first
        setTimeout(() => {
          fetchTodayAttendance();
        }, 500);
        
        if (activeTab === "attendance" || activeTab === "all_attendance") {
          fetchAttendance();
        }
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to check in",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error checking in:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to check in",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle check out
  const handleCheckOut = async () => {
    setSubmitting(true);
    try {
      // Get current IST time from the displayed currentDateTime
      const nowIST = new Date(currentDateTime.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
      const hours = String(nowIST.getHours()).padStart(2, '0');
      const minutes = String(nowIST.getMinutes()).padStart(2, '0');
      const seconds = String(nowIST.getSeconds()).padStart(2, '0');
      const checkOutTime = `${hours}:${minutes}:${seconds}`;
      
      // Get attendance type
      const attendanceType = getAttendanceType();
      
      const data = await checkOut(attendanceType);
      if (data.success && todayAttendance && todayAttendance.check_in_time) {
        // Calculate total hours from check-in to check-out
        const checkInDate = todayAttendance.date;
        const checkInTime = todayAttendance.check_in_time;
        
        // Parse check-in time
        const [checkInYear, checkInMonth, checkInDay] = checkInDate.split('-').map(Number);
        const [checkInHours, checkInMinutes, checkInSeconds = 0] = checkInTime.split(':').map(Number);
        const checkInDateTime = new Date(checkInYear, checkInMonth - 1, checkInDay, checkInHours, checkInMinutes, checkInSeconds);
        
        // Parse check-out time (use same date as check-in)
        const [checkOutHours, checkOutMinutes, checkOutSeconds = 0] = checkOutTime.split(':').map(Number);
        const checkOutDateTime = new Date(checkInYear, checkInMonth - 1, checkInDay, checkOutHours, checkOutMinutes, checkOutSeconds);
        
        // Calculate total hours
        const elapsedMs = checkOutDateTime.getTime() - checkInDateTime.getTime();
        const totalHours = Math.round((elapsedMs / (1000 * 60 * 60)) * 100) / 100; // Round to 2 decimal places
        
        // Immediately update todayAttendance with the IST time from currentDateTime
        setTodayAttendance({
          ...todayAttendance,
          check_out_time: checkOutTime,
          total_hours: totalHours
        });
        
        // Reset overtime checkbox after check-out
        setOvertimeEnabled(false);
        localStorage.setItem('overtimeEnabled', 'false');
        
        // Then fetch the complete attendance record
        setTimeout(() => {
          fetchTodayAttendance();
        }, 100);
        
        if (activeTab === "attendance" || activeTab === "all_attendance") {
          fetchAttendance();
        }
        fetchStats();
      } else {
        toast({
          title: "Error",
          description: data.error || "Failed to check out",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error checking out:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to check out",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Filtered attendance
  const filteredAttendance = useMemo(() => {
    let filtered = attendance;
    
    // Only show completed attendance records (with both check-in and check-out)
    filtered = filtered.filter(
      (item) => item.check_in_time && item.check_out_time
    );
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.user_name?.toLowerCase().includes(term) ||
          item.date?.includes(term) ||
          item.status?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  }, [attendance, searchTerm]);

  // Calculate statistics per doctor grouped by month for All Attendance view
  const doctorStats = useMemo(() => {
    if (activeTab !== "all_attendance" || !filteredAttendance.length) {
      return {};
    }

    // Group attendance by month first, then by doctor
    const groupedByMonth = {};
    
    filteredAttendance.forEach(item => {
      if (!item.date) return;
      
      // Extract month from date (YYYY-MM-DD format)
      const monthKey = item.date.substring(0, 7); // YYYY-MM
      const monthName = getMonthName(item.date);
      
      if (!groupedByMonth[monthKey]) {
        groupedByMonth[monthKey] = {
          monthKey,
          monthName,
          doctors: {}
        };
      }
      
      const doctorId = item.user_id;
      const doctorName = item.user_name || 'Unknown';
      
      if (!groupedByMonth[monthKey].doctors[doctorId]) {
        groupedByMonth[monthKey].doctors[doctorId] = {
          user_id: doctorId,
          user_name: doctorName,
          records: []
        };
      }
      
      groupedByMonth[monthKey].doctors[doctorId].records.push(item);
    });

    // Get current date in IST
    const now = new Date();
    const istDateString = now.toLocaleDateString("en-CA", {
      timeZone: "Asia/Kolkata"
    });
    
    // Calculate stats for each month
    const monthStats = {};
    
    Object.keys(groupedByMonth).forEach(monthKey => {
      const monthData = groupedByMonth[monthKey];
      
      // Filter based on week/month filter
      if (doctorStatsFilter === "week") {
        // Only show current week's month
        const currentMonthKey = istDateString.substring(0, 7);
        if (monthKey !== currentMonthKey) return;
      }
      // If filter is "month", show all months
      
      // Calculate date range for the month
      const [year, month] = monthKey.split('-').map(Number);
      const firstDayOfMonth = `${year}-${String(month).padStart(2, '0')}-01`;
      
      // Determine end date
      let endDate;
      if (monthKey === istDateString.substring(0, 7)) {
        // Current month - use current date
        endDate = istDateString;
      } else {
        // Past month - use last day of month
        const lastDay = new Date(year, month, 0).getDate();
        endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      }
      
      // Calculate working days for this month
      const totalDays = getWorkingDaysForDateRange(firstDayOfMonth, endDate);
      
      // Calculate stats for each doctor in this month
      const doctorStatsForMonth = Object.values(monthData.doctors).map(doctor => {
        const records = doctor.records.filter(r => {
          const recordDate = r.date;
          return recordDate >= firstDayOfMonth && recordDate <= endDate;
        });
        
        // Count present days (unique dates where doctor checked in)
        const presentDates = new Set(
          records
            .filter(r => r.check_in_time)
            .map(r => r.date)
        );
        const presentDays = presentDates.size;
        
        // Calculate absent days
        const absentDays = Math.max(0, totalDays - presentDays);
        
        // Calculate total hours
        const totalHours = records.reduce((sum, r) => {
          return sum + (parseFloat(r.total_hours) || 0);
        }, 0);
        
        // Calculate average hours per day
        const avgHoursPerDay = totalDays > 0 ? totalHours / totalDays : 0;
        
        return {
          ...doctor,
          total_days: totalDays,
          present_days: presentDays,
          absent_days: absentDays,
          total_hours: totalHours,
          avg_hours_per_day: avgHoursPerDay
        };
      }).sort((a, b) => a.user_name.localeCompare(b.user_name));
      
      monthStats[monthKey] = {
        monthName: monthData.monthName,
        doctors: doctorStatsForMonth
      };
    });
    
    return monthStats;
  }, [filteredAttendance, activeTab, doctorStatsFilter]);

  // Filter doctor stats based on search term (doctor name or month name)
  const filteredDoctorStats = useMemo(() => {
    if (!doctorStatsSearch.trim()) {
      return doctorStats;
    }

    const searchTerm = doctorStatsSearch.toLowerCase().trim();
    const filtered = {};

    Object.keys(doctorStats).forEach(monthKey => {
      const monthData = doctorStats[monthKey];
      
      // Check if month name matches search
      const monthMatches = monthData.monthName.toLowerCase().includes(searchTerm);
      
      // Filter doctors by name
      const filteredDoctors = monthData.doctors.filter(doctor => 
        doctor.user_name.toLowerCase().includes(searchTerm)
      );
      
      // Include month if month name matches OR if any doctor matches
      if (monthMatches || filteredDoctors.length > 0) {
        filtered[monthKey] = {
          ...monthData,
          doctors: monthMatches ? monthData.doctors : filteredDoctors
        };
      }
    });

    return filtered;
  }, [doctorStats, doctorStatsSearch]);

  // Calculate month-wise statistics for doctor's own attendance
  const myAttendanceStats = useMemo(() => {
    if (activeTab !== "attendance" || !filteredAttendance.length) {
      return {};
    }

    // Group attendance by month
    const groupedByMonth = {};
    
    filteredAttendance.forEach(item => {
      if (!item.date) return;
      
      // Extract month from date (YYYY-MM-DD format)
      const monthKey = item.date.substring(0, 7); // YYYY-MM
      const monthName = getMonthName(item.date);
      
      if (!groupedByMonth[monthKey]) {
        groupedByMonth[monthKey] = {
          monthKey,
          monthName,
          records: []
        };
      }
      
      groupedByMonth[monthKey].records.push(item);
    });

    // Get current date in IST
    const now = new Date();
    const istDateString = now.toLocaleDateString("en-CA", {
      timeZone: "Asia/Kolkata"
    });
    
    // Calculate stats for each month
    const monthStats = {};
    
    Object.keys(groupedByMonth).forEach(monthKey => {
      const monthData = groupedByMonth[monthKey];
      
      // Filter based on week/month filter
      if (myAttendanceFilter === "week") {
        // Only show current week's month
        const currentMonthKey = istDateString.substring(0, 7);
        if (monthKey !== currentMonthKey) return;
      }
      // If filter is "month", show all months
      
      // Calculate date range for the month
      const [year, month] = monthKey.split('-').map(Number);
      const firstDayOfMonth = `${year}-${String(month).padStart(2, '0')}-01`;
      
      // Determine end date
      let endDate;
      if (monthKey === istDateString.substring(0, 7)) {
        // Current month - use current date
        endDate = istDateString;
      } else {
        // Past month - use last day of month
        const lastDay = new Date(year, month, 0).getDate();
        endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      }
      
      // Calculate working days for this month
      const totalDays = getWorkingDaysForDateRange(firstDayOfMonth, endDate);
      
      // Filter records for this month
      const records = monthData.records.filter(r => {
        const recordDate = r.date;
        return recordDate >= firstDayOfMonth && recordDate <= endDate;
      });
      
      // Count present days (unique dates where doctor checked in)
      const presentDates = new Set(
        records
          .filter(r => r.check_in_time)
          .map(r => r.date)
      );
      const presentDays = presentDates.size;
      
      // Calculate absent days
      const absentDays = Math.max(0, totalDays - presentDays);
      
      // Calculate total hours
      const totalHours = records.reduce((sum, r) => {
        return sum + (parseFloat(r.total_hours) || 0);
      }, 0);
      
      // Calculate average hours per day
      const avgHoursPerDay = totalDays > 0 ? totalHours / totalDays : 0;
      
      monthStats[monthKey] = {
        monthName: monthData.monthName,
        total_days: totalDays,
        present_days: presentDays,
        absent_days: absentDays,
        total_hours: totalHours,
        avg_hours_per_day: avgHoursPerDay
      };
    });
    
    return monthStats;
  }, [filteredAttendance, activeTab, myAttendanceFilter]);

  // Filter my attendance stats based on search term (month name)
  const filteredMyAttendanceStats = useMemo(() => {
    if (!myAttendanceSearch.trim()) {
      return myAttendanceStats;
    }

    const searchTerm = myAttendanceSearch.toLowerCase().trim();
    const filtered = {};

    Object.keys(myAttendanceStats).forEach(monthKey => {
      const monthData = myAttendanceStats[monthKey];
      
      // Check if month name matches search
      if (monthData.monthName.toLowerCase().includes(searchTerm)) {
        filtered[monthKey] = monthData;
      }
    });

    return filtered;
  }, [myAttendanceStats, myAttendanceSearch]);

  // Filtered leave requests
  const filteredLeaveRequests = useMemo(() => {
    return leaveRequests;
  }, [leaveRequests]);

  const tabs = isAdmin || (viewAsAdmin && profile?.role === 'chief_doctor')
    ? [
        { id: "checkin", label: "Check In/Out", icon: Clock },
        { id: "all_attendance", label: "All Attendance", icon: Calendar },
        { id: "approve_leave", label: "Approve Leave", icon: CheckCircle },
      ]
    : [
        { id: "checkin", label: "Check In/Out", icon: Clock },
        { id: "attendance", label: "My Attendance", icon: Calendar },
        { id: "leave", label: "Leave Requests", icon: FileText },
      ];

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cls(
              "flex items-center space-x-2 px-4 py-2 rounded-md transition-colors whitespace-nowrap flex-shrink-0",
              activeTab === tab.id
                ? "bg-blue-600 text-white"
                : "text-gray-300 hover:text-white hover:bg-gray-700"
            )}
          >
            <tab.icon className="h-4 w-4" />
            <span className="text-sm">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Check In/Out Tab */}
      {activeTab === "checkin" && (
        <Card className="glass-effect border-gray-700">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex-1">
                <CardTitle className="text-white">Check In / Check Out</CardTitle>
                <CardDescription className="text-gray-400">
                  Office Hours: Mon-Sat 9:30 AM - 9:00 PM, Sunday 10:00 AM - 7:00 PM
                </CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="text-right">
                  <div className="p-3 bg-gray-800 rounded-lg border border-gray-700">
                    <p className="text-gray-400 text-xs mb-1">Current Date & Time (IST)</p>
                    <p className="text-white font-semibold text-sm sm:text-base">
                      {currentDateTime.toLocaleDateString("en-IN", {
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        timeZone: "Asia/Kolkata",
                      })}
                    </p>
                    <p className="text-blue-400 font-mono text-lg sm:text-xl font-bold">
                      {currentDateTime.toLocaleTimeString("en-IN", {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                        hour12: true,
                        timeZone: "Asia/Kolkata",
                      })}
                    </p>
                  </div>
                </div>
                {!viewAsAdmin && !isAdmin && todayAttendance && todayAttendance.check_in_time && !todayAttendance.check_out_time && (
                  <div className="text-right">
                    <div className="p-3 bg-gray-800 rounded-lg border border-gray-700">
                      <p className="text-gray-400 text-xs mb-1">
                        Working Time
                      </p>
                      {(() => {
                        try {
                          // Parse check-in time components
                          const attendanceDate = todayAttendance.date ? todayAttendance.date.split('T')[0] : null;
                          if (!attendanceDate || !todayAttendance.check_in_time) {
                            return (
                              <p className="text-gray-400 text-lg sm:text-xl font-bold">
                                0 mins
                              </p>
                            );
                          }
                          
                          const [year, month, day] = attendanceDate.split('-').map(Number);
                          const [hours, minutes, seconds = 0] = todayAttendance.check_in_time.split(':').map(Number);
                          
                          // Get current time in IST
                          const nowIST = new Date(currentDateTime.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
                          
                          // Create check-in time as Date object (treat as local timezone)
                          const checkInTime = new Date(year, month - 1, day, hours, minutes, seconds);
                          
                          // Get current time components from IST
                          const now = new Date(
                            nowIST.getFullYear(),
                            nowIST.getMonth(),
                            nowIST.getDate(),
                            nowIST.getHours(),
                            nowIST.getMinutes(),
                            nowIST.getSeconds()
                          );
                          
                          const elapsedMs = now.getTime() - checkInTime.getTime();
                          
                          // Only show if elapsed time is positive
                          if (elapsedMs < 0) {
                            return (
                              <p className="text-gray-400 text-lg sm:text-xl font-bold">
                                0 mins
                              </p>
                            );
                          }
                          
                          const elapsedHours = Math.floor(elapsedMs / (1000 * 60 * 60));
                          const elapsedMinutes = Math.floor((elapsedMs % (1000 * 60 * 60)) / (1000 * 60));
                          
                          // Display with "hrs" or "mins" format
                          if (elapsedHours === 0) {
                            // Less than 1 hour, show in minutes
                            return (
                              <p className="text-green-400 font-mono text-lg sm:text-xl font-bold">
                                {elapsedMinutes} {elapsedMinutes === 1 ? 'min' : 'mins'}
                              </p>
                            );
                          } else {
                            // 1 hour or more, show in hours and minutes
                            if (elapsedMinutes === 0) {
                              return (
                                <p className="text-green-400 font-mono text-lg sm:text-xl font-bold">
                                  {elapsedHours} {elapsedHours === 1 ? 'hr' : 'hrs'}
                                </p>
                              );
                            } else {
                              return (
                                <p className="text-green-400 font-mono text-lg sm:text-xl font-bold">
                                  {elapsedHours} {elapsedHours === 1 ? 'hr' : 'hrs'} {elapsedMinutes} {elapsedMinutes === 1 ? 'min' : 'mins'}
                                </p>
                              );
                            }
                          }
                        } catch (error) {
                          console.error('Error calculating working time:', error);
                          return (
                            <p className="text-gray-400 text-lg sm:text-xl font-bold">
                              0 mins
                            </p>
                          );
                        }
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Today's Status */}
              {/* For admins: Show all active check-ins */}
              {(isAdmin || (viewAsAdmin && profile?.role === 'chief_doctor')) && allActiveAttendance.length > 0 ? (
                <div className="space-y-4">
                  <h3 className="text-white font-semibold">Active Check-Ins</h3>
                  {allActiveAttendance.map((attendance) => (
                    <div key={attendance.id} className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                      <div className="flex justify-between items-start">
                        <div className="flex flex-wrap items-start gap-6 flex-1">
                          {attendance.user_name && (
                            <div className="flex flex-col">
                              <p className="text-gray-400 text-sm">Doctor</p>
                              <p className="text-white font-medium">
                                {attendance.user_name}
                              </p>
                            </div>
                          )}
                          <div className="flex flex-col">
                            <p className="text-gray-400 text-sm">Date</p>
                            <p className="text-white font-medium">
                              {(() => {
                                // Show current date (IST) for consistency with "Current Date & Time (IST)"
                                return currentDateTime.toLocaleDateString("en-IN", {
                                  year: "numeric",
                                  month: "short",
                                  day: "2-digit",
                                  timeZone: "Asia/Kolkata",
                                });
                              })()}
                            </p>
                          </div>
                          <div className="flex flex-col">
                            <p className="text-gray-400 text-sm">Check In</p>
                            <p className="text-white font-medium">
                              {attendance.check_in_time ? formatTime(attendance.check_in_time) : "Not checked in"}
                            </p>
                          </div>
                          <div className="flex flex-col">
                            <p className="text-gray-400 text-sm">Check Out</p>
                            <p className="text-white font-medium">
                              {attendance.check_out_time ? formatTime(attendance.check_out_time) : "Not checked out"}
                            </p>
                          </div>
                          <div className="flex flex-col">
                            <p className="text-gray-400 text-sm">Attendance Type</p>
                            <p className="text-white font-medium">
                              {attendance.attendance_type || 'Regular'}
                            </p>
                          </div>
                          {/* Working Time - calculate for each doctor */}
                          {attendance.check_in_time && !attendance.check_out_time && (
                            <div className="flex flex-col">
                              <p className="text-gray-400 text-sm">Working Time</p>
                              <p className="text-green-400 font-medium">
                                {(() => {
                                  try {
                                    // Parse check-in time components
                                    const attendanceDate = attendance.date ? attendance.date.split('T')[0] : null;
                                    if (!attendanceDate || !attendance.check_in_time) {
                                      return "0 mins";
                                    }
                                    
                                    const [year, month, day] = attendanceDate.split('-').map(Number);
                                    const [hours, minutes, seconds = 0] = attendance.check_in_time.split(':').map(Number);
                                    
                                    // Get current time in IST
                                    const nowIST = new Date(currentDateTime.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
                                    
                                    // Create check-in time as Date object
                                    const checkInTime = new Date(year, month - 1, day, hours, minutes, seconds);
                                    
                                    // Get current time components from IST
                                    const now = new Date(
                                      nowIST.getFullYear(),
                                      nowIST.getMonth(),
                                      nowIST.getDate(),
                                      nowIST.getHours(),
                                      nowIST.getMinutes(),
                                      nowIST.getSeconds()
                                    );
                                    
                                    const elapsedMs = now.getTime() - checkInTime.getTime();
                                    
                                    if (elapsedMs < 0) {
                                      return "0 mins";
                                    }
                                    
                                    const elapsedHours = Math.floor(elapsedMs / (1000 * 60 * 60));
                                    const elapsedMinutes = Math.floor((elapsedMs % (1000 * 60 * 60)) / (1000 * 60));
                                    
                                    // Display with "hrs" or "mins" format
                                    if (elapsedHours === 0) {
                                      return `${elapsedMinutes} ${elapsedMinutes === 1 ? 'min' : 'mins'}`;
                                    } else {
                                      if (elapsedMinutes === 0) {
                                        return `${elapsedHours} ${elapsedHours === 1 ? 'hr' : 'hrs'}`;
                                      } else {
                                        return `${elapsedHours} ${elapsedHours === 1 ? 'hr' : 'hrs'} ${elapsedMinutes} ${elapsedMinutes === 1 ? 'min' : 'mins'}`;
                                      }
                                    }
                                  } catch (error) {
                                    console.error('Error calculating working time:', error);
                                    return "0 mins";
                                  }
                                })()}
                              </p>
                            </div>
                          )}
                          {attendance.total_hours && (
                            <div className="flex flex-col">
                              <p className="text-gray-400 text-sm">Total Hours</p>
                              <p className="text-white font-medium">
                                {formatTotalHours(attendance.total_hours)}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedItem(attendance);
                              setEditModalOpen(true);
                            }}
                            className="text-blue-400 hover:text-blue-300 hover:bg-gray-700"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedItem(attendance);
                              setDeleteModalOpen(true);
                            }}
                            className="text-red-400 hover:text-red-300 hover:bg-gray-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* For regular doctors: Show their own attendance */
                todayAttendance && todayAttendance.check_in_time && !todayAttendance.check_out_time && (
                  <div className="p-4 bg-gray-800 rounded-lg border border-gray-700">
                    <h3 className="text-white font-semibold mb-3">Today's Status</h3>
                    <div className="flex flex-wrap items-start gap-6">
                      <div className="flex flex-col">
                        <p className="text-gray-400 text-sm">Date</p>
                        <p className="text-white font-medium">
                          {(() => {
                            // Show current date (IST) for consistency with "Current Date & Time (IST)"
                            return currentDateTime.toLocaleDateString("en-IN", {
                              year: "numeric",
                              month: "short",
                              day: "2-digit",
                              timeZone: "Asia/Kolkata",
                            });
                          })()}
                        </p>
                      </div>
                      <div className="flex flex-col">
                        <p className="text-gray-400 text-sm">Check In</p>
                        <p className="text-white font-medium">
                          {todayAttendance.check_in_time ? formatTime(todayAttendance.check_in_time) : "Not checked in"}
                        </p>
                      </div>
                      <div className="flex flex-col">
                        <p className="text-gray-400 text-sm">Check Out</p>
                        <p className="text-white font-medium">
                          {todayAttendance.check_out_time ? formatTime(todayAttendance.check_out_time) : "Not checked out"}
                        </p>
                      </div>
                      {todayAttendance.total_hours && (
                        <div className="flex flex-col">
                          <p className="text-gray-400 text-sm">Total Hours</p>
                          <p className="text-white font-medium">
                            {formatTotalHours(todayAttendance.total_hours)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              )}

              {/* Overtime Checkbox */}
              {/* Show if outside office hours OR if user is checked in with overtime type */}
              {(!isWithinOfficeHours || (todayAttendance && todayAttendance.check_in_time && !todayAttendance.check_out_time && todayAttendance.attendance_type === 'Overtime')) && (
                <div className="p-4 bg-yellow-900/20 border border-yellow-700 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="overtime"
                      checked={overtimeEnabled}
                      onCheckedChange={(checked) => {
                        setOvertimeEnabled(checked);
                        localStorage.setItem('overtimeEnabled', checked ? 'true' : 'false');
                      }}
                      disabled={todayAttendance && todayAttendance.check_out_time}
                      className="border-yellow-600"
                    />
                    <Label htmlFor="overtime" className="text-yellow-400 text-sm cursor-pointer">
                      Enable Overtime (Outside office hours)
                    </Label>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={handleCheckIn}
                  disabled={submitting || (todayAttendance && todayAttendance.check_in_time && !todayAttendance.check_out_time) || (!isWithinOfficeHours && !overtimeEnabled)}
                  className="flex-1 medical-gradient text-white hover:opacity-90 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="animate-spin mr-2 h-4 w-4" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <LogIn className="mr-2 h-4 w-4" />
                      Check In
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleCheckOut}
                  disabled={submitting || !todayAttendance || !todayAttendance.check_in_time || todayAttendance.check_out_time || (!isWithinOfficeHours && !overtimeEnabled)}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="animate-spin mr-2 h-4 w-4" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <LogOut className="mr-2 h-4 w-4" />
                      Check Out
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Attendance / All Attendance Tab */}
      {(activeTab === "attendance" || activeTab === "all_attendance") && (
        <div className="space-y-4">
          {/* My Attendance Statistics - Month-wise display */}
          {activeTab === "attendance" && Object.keys(filteredMyAttendanceStats).length > 0 && (
            <Card className="glass-effect border-gray-700">
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="text-white">My Attendance Statistics</CardTitle>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Label className="text-white text-sm whitespace-nowrap">Search:</Label>
                      <div className="relative flex-1 sm:w-48">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          className="bg-gray-800 border-gray-600 text-white pl-10 w-full"
                          placeholder="Search by month..."
                          value={myAttendanceSearch}
                          onChange={(e) => setMyAttendanceSearch(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-white text-sm">Filter:</Label>
                      <select
                        className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-white text-sm focus:ring-2 focus:ring-blue-500"
                        value={myAttendanceFilter}
                        onChange={(e) => setMyAttendanceFilter(e.target.value)}
                      >
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                      </select>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {Object.keys(filteredMyAttendanceStats).length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No results found matching your search</p>
                ) : (
                <div className="space-y-6">
                  {Object.keys(filteredMyAttendanceStats).sort().reverse().map(monthKey => {
                    const monthData = filteredMyAttendanceStats[monthKey];
                    
                    return (
                      <div key={monthKey} className={`space-y-3 rounded-lg p-4 ${getMonthGradient(monthData.monthName)}`}>
                        <h3 className="text-lg font-semibold text-white border-b border-gray-700/50 pb-2 text-center">
                          {monthData.monthName}
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-gray-700">
                                <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Total Days</th>
                                <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Present Days</th>
                                <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Absent Days</th>
                                <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Total Hours</th>
                                <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Avg Hours/Day</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b border-gray-800 hover:bg-gray-800/50">
                                <td className="py-3 px-4 text-white text-sm">{monthData.total_days}</td>
                                <td className="py-3 px-4 text-green-400 text-sm font-medium">{monthData.present_days}</td>
                                <td className="py-3 px-4 text-red-400 text-sm font-medium">{monthData.absent_days}</td>
                                <td className="py-3 px-4 text-blue-400 text-sm">{formatTotalHours(monthData.total_hours)}</td>
                                <td className="py-3 px-4 text-purple-400 text-sm">{formatTotalHours(monthData.avg_hours_per_day)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Doctor Statistics for All Attendance */}
          {activeTab === "all_attendance" && Object.keys(doctorStats).length > 0 && (
            <Card className="glass-effect border-gray-700">
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="text-white">Attendance Statistics by Doctor</CardTitle>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Label className="text-white text-sm whitespace-nowrap">Search:</Label>
                      <div className="relative flex-1 sm:w-48">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          className="bg-gray-800 border-gray-600 text-white pl-10 w-full"
                          placeholder="Search by doctor or month..."
                          value={doctorStatsSearch}
                          onChange={(e) => setDoctorStatsSearch(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-white text-sm">Filter:</Label>
                      <select
                        className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-white text-sm focus:ring-2 focus:ring-blue-500"
                        value={doctorStatsFilter}
                        onChange={(e) => setDoctorStatsFilter(e.target.value)}
                      >
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                      </select>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {Object.keys(filteredDoctorStats).length === 0 ? (
                  <p className="text-gray-400 text-center py-8">No results found matching your search</p>
                ) : (
                <div className="space-y-6">
                  {Object.keys(filteredDoctorStats).sort().reverse().map(monthKey => {
                    const monthData = filteredDoctorStats[monthKey];
                    if (!monthData.doctors || monthData.doctors.length === 0) return null;
                    
                    return (
                      <div key={monthKey} className={`space-y-3 rounded-lg p-4 ${getMonthGradient(monthData.monthName)}`}>
                        <h3 className="text-lg font-semibold text-white border-b border-gray-700/50 pb-2 text-center">
                          {monthData.monthName}
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-gray-700">
                                <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Doctor</th>
                                <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Total Days</th>
                                <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Present Days</th>
                                <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Absent Days</th>
                                <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Total Hours</th>
                                <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Avg Hours/Day</th>
                              </tr>
                            </thead>
                            <tbody>
                              {monthData.doctors.map((doctor) => (
                                <tr key={doctor.user_id} className="border-b border-gray-800 hover:bg-gray-800/50">
                                  <td className="py-3 px-4 text-white text-sm font-medium">{doctor.user_name}</td>
                                  <td className="py-3 px-4 text-white text-sm">{doctor.total_days}</td>
                                  <td className="py-3 px-4 text-green-400 text-sm font-medium">{doctor.present_days}</td>
                                  <td className="py-3 px-4 text-red-400 text-sm font-medium">{doctor.absent_days}</td>
                                  <td className="py-3 px-4 text-blue-400 text-sm">{formatTotalHours(doctor.total_hours)}</td>
                                  <td className="py-3 px-4 text-purple-400 text-sm">{formatTotalHours(doctor.avg_hours_per_day)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Attendance List */}
          <Card className="glass-effect border-gray-700">
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <CardTitle className="text-white">
                  {activeTab === "all_attendance" ? "All Attendance Records" : "My Attendance"}
                </CardTitle>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
                  <div className="flex items-center gap-2">
                    <Label className="text-white text-sm whitespace-nowrap">Filter:</Label>
                    <select
                      className="bg-gray-800 border border-gray-600 rounded-lg px-3 py-1.5 text-white text-sm focus:ring-2 focus:ring-blue-500"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                    >
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                      <option value="date">Specific Date</option>
                    </select>
                  </div>
                  {filter === "date" && (
                    <div className="flex items-center gap-2">
                      <Label className="text-white text-sm whitespace-nowrap">Date:</Label>
                      <Input
                        type="date"
                        className="bg-gray-800 border-gray-600 text-white w-auto"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <Label className="text-white text-sm whitespace-nowrap">Search:</Label>
                    <div className="relative flex-1 sm:w-48">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        className="bg-gray-800 border-gray-600 text-white pl-10 w-full"
                        placeholder="Search by name, date..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="animate-spin h-6 w-6 text-gray-400" />
                </div>
              ) : filteredAttendance.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No attendance records found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        {activeTab === "all_attendance" && (
                          <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Doctor</th>
                        )}
                        <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Date</th>
                        <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Check In</th>
                        <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Check Out</th>
                        <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Hours</th>
                        <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Status</th>
                        {activeTab === "all_attendance" && (
                          <th className="text-left py-3 px-4 text-gray-400 text-sm font-medium">Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAttendance.map((item) => (
                        <tr key={item.id} className="border-b border-gray-800 hover:bg-gray-800/50">
                          {activeTab === "all_attendance" && (
                            <td className="py-3 px-4 text-white text-sm">{item.user_name || "—"}</td>
                          )}
                          <td className="py-3 px-4 text-white text-sm">{formatDate(item.date)}</td>
                          <td className="py-3 px-4 text-white text-sm">{formatTime(item.check_in_time)}</td>
                          <td className="py-3 px-4 text-white text-sm">{formatTime(item.check_out_time)}</td>
                          <td className="py-3 px-4 text-white text-sm">{formatTotalHours(item.total_hours)}</td>
                          <td className="py-3 px-4">
                            <span
                              className={cls(
                                "px-2 py-1 rounded text-xs",
                                item.status === "Present"
                                  ? "bg-green-500/20 text-green-400"
                                  : item.status === "Absent"
                                  ? "bg-red-500/20 text-red-400"
                                  : item.status === "Leave"
                                  ? "bg-blue-500/20 text-blue-400"
                                  : "bg-yellow-500/20 text-yellow-400"
                              )}
                            >
                              {item.status || "—"}
                            </span>
                          </td>
                          {activeTab === "all_attendance" && (
                            <td className="py-3 px-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setEditModalOpen(true);
                                }}
                                className="text-blue-400 hover:text-blue-300"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Leave Requests Tab */}
      {activeTab === "leave" && (
        <div className="space-y-4">
          <Card className="glass-effect border-gray-700">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-white">My Leave Requests</CardTitle>
                <Button
                  onClick={() => {
                    setSelectedItem(null);
                    setLeaveModalOpen(true);
                  }}
                  className="medical-gradient text-white"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Request Leave
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Label className="text-white mb-2">Filter by Status</Label>
                <select
                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                  value={leaveStatusFilter}
                  onChange={(e) => setLeaveStatusFilter(e.target.value)}
                >
                  <option value="all">All</option>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Denied">Denied</option>
                </select>
              </div>
              {loading ? (
                <div className="flex justify-center py-8">
                  <RefreshCw className="animate-spin h-6 w-6 text-gray-400" />
                </div>
              ) : filteredLeaveRequests.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No leave requests found</p>
              ) : (
                <div className="space-y-3">
                  {filteredLeaveRequests.map((request) => (
                    <div
                      key={request.id}
                      className="p-4 bg-gray-800 rounded-lg border border-gray-700"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-white font-semibold">{request.leave_type}</span>
                            <span
                              className={cls(
                                "px-2 py-1 rounded text-xs",
                                request.status === "Approved"
                                  ? "bg-green-500/20 text-green-400"
                                  : request.status === "Denied"
                                  ? "bg-red-500/20 text-red-400"
                                  : "bg-yellow-500/20 text-yellow-400"
                              )}
                            >
                              {request.status}
                            </span>
                          </div>
                          <p className="text-gray-400 text-sm">
                            {formatDate(request.start_date)} - {formatDate(request.end_date)}
                          </p>
                          <p className="text-white text-sm mt-2">{request.reason}</p>
                          {request.admin_notes && (
                            <p className="text-gray-400 text-sm mt-2">Admin Notes: {request.admin_notes}</p>
                          )}
                          {request.approved_by_name && (
                            <p className="text-gray-400 text-sm mt-2">
                              Approved by: <span className="text-white">{request.approved_by_name}</span>
                              {request.approved_at && (
                                <span className="ml-2">on {formatDateTime(request.approved_at)}</span>
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Approve Leave Tab (Admin) */}
      {activeTab === "approve_leave" && (
        <Card className="glass-effect border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Approve Leave Requests</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Label className="text-white mb-2">Filter by Status</Label>
              <select
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                value={leaveStatusFilter}
                onChange={(e) => setLeaveStatusFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Denied">Denied</option>
              </select>
            </div>
            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="animate-spin h-6 w-6 text-gray-400" />
              </div>
            ) : filteredLeaveRequests.filter(r => leaveStatusFilter === 'all' || r.status === leaveStatusFilter).length === 0 ? (
              <p className="text-gray-400 text-center py-8">No leave requests found</p>
            ) : (
              <div className="space-y-3">
                {filteredLeaveRequests
                  .filter(r => leaveStatusFilter === 'all' || r.status === leaveStatusFilter)
                  .map((request) => (
                    <div
                      key={request.id}
                      className="p-4 bg-gray-800 rounded-lg border border-gray-700"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-white font-semibold">{request.user_name || "—"}</span>
                            <span className="text-gray-400 text-sm">{request.leave_type}</span>
                            <span
                              className={cls(
                                "px-2 py-1 rounded text-xs",
                                request.status === "Approved"
                                  ? "bg-green-500/20 text-green-400"
                                  : request.status === "Denied"
                                  ? "bg-red-500/20 text-red-400"
                                  : "bg-yellow-500/20 text-yellow-400"
                              )}
                            >
                              {request.status}
                            </span>
                          </div>
                          <p className="text-gray-400 text-sm">
                            {formatDate(request.start_date)} - {formatDate(request.end_date)}
                          </p>
                          <p className="text-white text-sm mt-2">{request.reason}</p>
                          {request.admin_notes && (
                            <p className="text-gray-400 text-sm mt-2">Admin Notes: {request.admin_notes}</p>
                          )}
                          {request.approved_by_name && (
                            <p className="text-gray-400 text-sm mt-2">
                              Approved by: <span className="text-white">{request.approved_by_name}</span>
                              {request.approved_at && (
                                <span className="ml-2">on {formatDateTime(request.approved_at)}</span>
                              )}
                            </p>
                          )}
                        </div>
                        {request.status === "Pending" && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedItem(request);
                                setApproveModalOpen(true);
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedItem(request);
                                setApproveModalOpen(true);
                              }}
                              className="bg-red-600 hover:bg-red-700 text-white"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Deny
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Leave Request Modal */}
      <Dialog open={leaveModalOpen} onOpenChange={setLeaveModalOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Request Leave</DialogTitle>
          </DialogHeader>
          <LeaveRequestForm
            initial={selectedItem}
            onSubmit={async (payload) => {
              setSubmitting(true);
              try {
                const data = await submitLeaveRequest(payload);
                if (data.success) {
                  toast({
                    title: "Success",
                    description: "Leave request submitted successfully",
                  });
                  setLeaveModalOpen(false);
                  setSelectedItem(null);
                  fetchLeaveRequests();
                } else {
                  throw new Error(data.error || "Failed to submit leave request");
                }
              } catch (error) {
                toast({
                  title: "Error",
                  description: error.message || "Failed to submit leave request",
                  variant: "destructive",
                });
              } finally {
                setSubmitting(false);
              }
            }}
            submitting={submitting}
            onCancel={() => {
              setLeaveModalOpen(false);
              setSelectedItem(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Approve/Deny Leave Modal */}
      <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              {selectedItem && (selectedItem.status === "Pending" ? "Approve/Deny Leave" : "View Leave Request")}
            </DialogTitle>
          </DialogHeader>
          <ApproveLeaveForm
            request={selectedItem}
            onSubmit={async (status, adminNotes) => {
              setSubmitting(true);
              try {
                const data = await approveLeaveRequest(selectedItem.id, status, adminNotes);
                if (data.success) {
                  toast({
                    title: "Success",
                    description: `Leave request ${status.toLowerCase()} successfully`,
                  });
                  setApproveModalOpen(false);
                  setSelectedItem(null);
                  fetchLeaveRequests();
                } else {
                  throw new Error(data.error || "Failed to update leave request");
                }
              } catch (error) {
                toast({
                  title: "Error",
                  description: error.message || "Failed to update leave request",
                  variant: "destructive",
                });
              } finally {
                setSubmitting(false);
              }
            }}
            submitting={submitting}
            onCancel={() => {
              setApproveModalOpen(false);
              setSelectedItem(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Attendance Modal (Admin) */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Attendance</DialogTitle>
          </DialogHeader>
          <EditAttendanceForm
            attendance={selectedItem}
            onSubmit={async (payload) => {
              setSubmitting(true);
              try {
                const data = await updateAttendance(selectedItem.id, payload);
                if (data.success) {
                  toast({
                    title: "Success",
                    description: "Attendance updated successfully",
                  });
                  setEditModalOpen(false);
                  setSelectedItem(null);
                  fetchTodayAttendance();
                  fetchAttendance();
                  fetchStats();
                } else {
                  throw new Error(data.error || "Failed to update attendance");
                }
              } catch (error) {
                toast({
                  title: "Error",
                  description: error.message || "Failed to update attendance",
                  variant: "destructive",
                });
              } finally {
                setSubmitting(false);
              }
            }}
            submitting={submitting}
            onCancel={() => {
              setEditModalOpen(false);
              setSelectedItem(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Attendance Record</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-300">
              Are you sure you want to delete this attendance record?
            </p>
            {selectedItem && (
              <div className="p-3 bg-gray-700 rounded-lg">
                <p className="text-white"><span className="text-gray-400">Doctor:</span> {selectedItem.user_name}</p>
                <p className="text-white"><span className="text-gray-400">Date:</span> {formatDate(selectedItem.date)}</p>
                <p className="text-white"><span className="text-gray-400">Check In:</span> {formatTime(selectedItem.check_in_time)}</p>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setSelectedItem(null);
                }}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                No
              </Button>
              <Button
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    const data = await deleteAttendance(selectedItem.id);
                    if (data.success) {
                      toast({
                        title: "Success",
                        description: "Attendance record deleted successfully",
                      });
                      setDeleteModalOpen(false);
                      setSelectedItem(null);
                      fetchTodayAttendance();
                      fetchAttendance();
                      fetchStats();
                    } else {
                      throw new Error(data.error || "Failed to delete attendance");
                    }
                  } catch (error) {
                    toast({
                      title: "Error",
                      description: error.message || "Failed to delete attendance",
                      variant: "destructive",
                    });
                  } finally {
                    setSubmitting(false);
                  }
                }}
                disabled={submitting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {submitting ? "Deleting..." : "Yes, Delete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Leave Request Form Component
const LeaveRequestForm = ({ initial, onSubmit, submitting, onCancel }) => {
  const [leaveType, setLeaveType] = useState(initial?.leave_type || "");
  const [startDate, setStartDate] = useState(initial?.start_date || "");
  const [endDate, setEndDate] = useState(initial?.end_date || "");
  const [reason, setReason] = useState(initial?.reason || "");
  const [err, setErr] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!leaveType || !startDate || !endDate || !reason.trim()) {
      return setErr("All fields are required");
    }

    if (new Date(startDate) > new Date(endDate)) {
      return setErr("Start date cannot be after end date");
    }

    try {
      await onSubmit({ leave_type: leaveType, start_date: startDate, end_date: endDate, reason: reason.trim() });
    } catch (e) {
      setErr(e?.message || "Failed to submit");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!!err && (
        <div className="p-3 rounded-lg border border-rose-500/50 bg-rose-500/10 text-rose-400 text-sm">
          {err}
        </div>
      )}
      <div>
        <Label htmlFor="leaveType" className="text-white mb-2">Leave Type *</Label>
        <select
          id="leaveType"
          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
          value={leaveType}
          onChange={(e) => setLeaveType(e.target.value)}
          required
        >
          <option value="">Select type...</option>
          {LEAVE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <Label htmlFor="startDate" className="text-white mb-2">Start Date *</Label>
        <Input
          id="startDate"
          type="date"
          className="bg-gray-800 border-gray-600 text-white"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="endDate" className="text-white mb-2">End Date *</Label>
        <Input
          id="endDate"
          type="date"
          className="bg-gray-800 border-gray-600 text-white"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="reason" className="text-white mb-2">Reason *</Label>
        <textarea
          id="reason"
          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:ring-2 focus:ring-blue-500 resize-none"
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for leave..."
          required
        />
      </div>
      <div className="flex gap-3 pt-2">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={submitting}
          className={cls(
            "flex-1 medical-gradient text-white hover:opacity-90",
            submitting && "opacity-50 cursor-not-allowed"
          )}
        >
          {submitting ? (
            <>
              <RefreshCw className="animate-spin mr-2 h-4 w-4" />
              Submitting...
            </>
          ) : (
            <>
              <Plus className="mr-2 h-4 w-4" />
              Submit Request
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

// Approve Leave Form Component
const ApproveLeaveForm = ({ request, onSubmit, submitting, onCancel }) => {
  const [adminNotes, setAdminNotes] = useState(request?.admin_notes || "");
  const [action, setAction] = useState("");

  if (!request) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!action) return;
    try {
      await onSubmit(action === "approve" ? "Approved" : "Denied", adminNotes);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="p-4 bg-gray-700 rounded-lg">
        <p className="text-white font-semibold mb-2">{request.user_name || "—"}</p>
        <p className="text-gray-300 text-sm mb-1">{request.leave_type}</p>
        <p className="text-gray-400 text-sm">
          {formatDate(request.start_date)} - {formatDate(request.end_date)}
        </p>
        <p className="text-white text-sm mt-3">{request.reason}</p>
      </div>
      {request.status === "Pending" ? (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="adminNotes" className="text-white mb-2">Admin Notes (Optional)</Label>
            <textarea
              id="adminNotes"
              className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:ring-2 focus:ring-blue-500 resize-none"
              rows={3}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add notes..."
            />
          </div>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={() => setAction("approve")}
              disabled={submitting}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Approve
            </Button>
            <Button
              type="submit"
              onClick={() => setAction("deny")}
              disabled={submitting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Deny
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <p className="text-gray-400 text-sm mb-2">Status: {request.status}</p>
          {request.admin_notes && (
            <p className="text-gray-300 text-sm">Admin Notes: {request.admin_notes}</p>
          )}
          <Button
            variant="outline"
            onClick={onCancel}
            className="w-full mt-4 border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            Close
          </Button>
        </div>
      )}
    </div>
  );
};

// Edit Attendance Form Component
const EditAttendanceForm = ({ attendance, onSubmit, submitting, onCancel }) => {
  // Get current time in IST for default checkout time
  const getCurrentISTTime = () => {
    const now = new Date();
    const istTime = now.toLocaleTimeString("en-US", {
      timeZone: "Asia/Kolkata",
      hour12: false,
      hour: "2-digit",
      minute: "2-digit"
    });
    return istTime;
  };

  const [checkInTime, setCheckInTime] = useState(attendance?.check_in_time?.substring(0, 5) || "");
  const [checkOutTime, setCheckOutTime] = useState(
    attendance?.check_out_time?.substring(0, 5) || getCurrentISTTime()
  );
  const [status, setStatus] = useState(attendance?.status || "Present");
  const [notes, setNotes] = useState(attendance?.notes || "");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (attendance) {
      setCheckInTime(attendance.check_in_time?.substring(0, 5) || "");
      // If no checkout time exists, use current IST time as default
      setCheckOutTime(attendance.check_out_time?.substring(0, 5) || getCurrentISTTime());
      setStatus(attendance.status || "Present");
      setNotes(attendance.notes || "");
    }
  }, [attendance]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (checkInTime && checkOutTime && checkInTime >= checkOutTime) {
      return setErr("Check out time must be after check in time");
    }

    try {
      const payload = {};
      if (checkInTime) payload.check_in_time = checkInTime + ":00";
      if (checkOutTime) payload.check_out_time = checkOutTime + ":00";
      if (status) payload.status = status;
      if (notes !== undefined) payload.notes = notes;

      await onSubmit(payload);
    } catch (e) {
      setErr(e?.message || "Failed to update");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!!err && (
        <div className="p-3 rounded-lg border border-rose-500/50 bg-rose-500/10 text-rose-400 text-sm">
          {err}
        </div>
      )}
      <div>
        <Label htmlFor="checkInTime" className="text-white mb-2">Check In Time</Label>
        <Input
          id="checkInTime"
          type="time"
          className="bg-gray-800 border-gray-600 text-white"
          value={checkInTime}
          onChange={(e) => setCheckInTime(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="checkOutTime" className="text-white mb-2">Check Out Time</Label>
        <Input
          id="checkOutTime"
          type="time"
          className="bg-gray-800 border-gray-600 text-white"
          value={checkOutTime}
          onChange={(e) => setCheckOutTime(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="status" className="text-white mb-2">Status</Label>
        <select
          id="status"
          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <div>
        <Label htmlFor="notes" className="text-white mb-2">Notes</Label>
        <textarea
          id="notes"
          className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white text-sm focus:ring-2 focus:ring-blue-500 resize-none"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add notes..."
        />
      </div>
      <div className="flex gap-3 pt-2">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={submitting}
          className={cls(
            "flex-1 medical-gradient text-white hover:opacity-90",
            submitting && "opacity-50 cursor-not-allowed"
          )}
        >
          {submitting ? (
            <>
              <RefreshCw className="animate-spin mr-2 h-4 w-4" />
              Updating...
            </>
          ) : (
            <>
              <Edit className="mr-2 h-4 w-4" />
              Update
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default Attendance;

